import React, { useState } from "react";
import { Input, Button, Box, UnorderedList, Alert, Spinner, Text } from "@postenbring/hedwig-react";
import moment from "moment";

moment.updateLocale("nb", {
    calendar: {
        sameDay: "[i dag] dddd D. MMMM",
        nextDay: "[i morgen] dddd D. MMMM",
        nextWeek: "dddd D. MMMM",
        lastDay: "dddd D. MMMM",
        lastWeek: "dddd D. MMMM",
        sameElse: "dddd D. MMMM",
    },
});
moment.updateLocale("en", {
    calendar: {
        sameDay: "[today] dddd MMMM D",
        nextDay: "[tomorrow] dddd MMMM D",
        nextWeek: "dddd MMMM D",
        lastDay: "dddd MMMM D",
        lastWeek: "dddd MMMM D",
        sameElse: "dddd MMMM D",
    },
});

type MailboxDeliveryProps = {
    title: string;
    description: string;
    postCodeLabel: string;
    submitBtnLabel: string;
    resetBtnLabel: string;
    postCodeErrorMsg: string;
    deliveryDaysErrorMsg: string;
    nextDeliveryMsg: string;
    nextDeliveryWeekMsg: string;
    addressText: string;
    backText: string;
    serviceUrl: string;
    lang: "no" | "en";
};

export const MailboxDelivery = ({
    title,
    description,
    postCodeLabel,
    submitBtnLabel,
    resetBtnLabel,
    postCodeErrorMsg,
    deliveryDaysErrorMsg,
    nextDeliveryMsg,
    nextDeliveryWeekMsg,
    addressText,
    backText,
    serviceUrl,
    lang,
}: MailboxDeliveryProps) => {
    const [postCode, setPostCode] = useState("");
    const [postCodeError, setPostCodeError] = useState(false);
    const [selectedCity, setSelectedCity] = useState("");
    const [nextDeliveryDays, setNextDeliveryDays] = useState<string[]>([]);
    const [isResult, setIsResult] = useState(false);
    const [isLoading, setIsloading] = useState(false);
    const [isValidPostCode, setIsValidPostCode] = useState(false);

    function onSendReq(e) {
        if (e) e.preventDefault();

        if (!isValidPostCode || postCode.length !== 4) {
            setPostCodeError(true);
            return;
        }

        const reqUrl = `${serviceUrl}?postalCode=${postCode}`;
        moment.locale("en");
        setIsloading(true);

        fetch(reqUrl)
            .then((res) => (res.ok ? res.json() : Promise.reject(res)))
            .then((res) => {
                setIsResult(true);
                setIsloading(false);
                setNextDeliveryDays(getDeliveryDays(res));
            })
            .catch(() => {
                setIsloading(false);
                setIsResult(false);
            });
    }

    function getDeliveryDays(res) {
        moment.locale(lang === "no" ? "nb" : "en");
        return res?.delivery_dates
            ? res.delivery_dates.map((delDate) => moment(delDate).calendar())
            : [];
    }

    function onReset() {
        setIsResult(false);
        setNextDeliveryDays([]);
        setPostCode("");
        setSelectedCity("");
        setPostCodeError(false);
        setIsValidPostCode(false);
    }

    function onPostCodeInput(e) {
        const value = e.target.value;

        if (!/^\d{0,4}$/.test(value)) return;

        setPostCode(value);
        setPostCodeError(false);

        if (value.length === 4) {
            fetchCity(value);
        } else {
            return;
        }
    }

    function fetchCity(postalCode) {
        const postalUrl = `https://adressesok.posten.no/api/v1/addresses.json?address=postnummer:${postalCode}`;

        fetch(postalUrl, {
            headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        })
            .then((res) => (res.ok ? res.json() : Promise.reject(res)))
            .then((res) => {
                if (res?.addresses?.length > 0) {
                    setSelectedCity(res.addresses[0].city);
                    setIsValidPostCode(true);
                } else {
                    setPostCodeError(true);
                    setIsValidPostCode(false);
                }
            })
            .catch(() => {
                setPostCodeError(true);
                setIsValidPostCode(false);
            });
    }

    function handleKeyPress(e) {
        if (e.key === "Enter" && !isValidPostCode) {
            e.preventDefault();
        } else if (e.key === "Enter" && isValidPostCode) {
            onSendReq(e);
        }
    }

    if (isResult) {
        return (
            <Box variant="lighter">
                <Text variant="h3-title" as="h3" className="hds-mb-20-24">
                    {title}
                </Text>
                {nextDeliveryDays.length > 0 ? (
                    <>
                        <div className="hds-mb-20-24">
                            <p className="hds-mb-20-24">
                                <Text variant="body">
                                    <>
                                        {nextDeliveryMsg}{" "}
                                        <strong
                                            className="delivery-days-date"
                                            style={{ color: "var(--hds-colors-dark)" }}
                                        >
                                            {" "}
                                            {nextDeliveryDays[0]}
                                        </strong>
                                    </>
                                </Text>
                            </p>
                            <Text variant="body-title" className="hds-mb-8-12">
                                {addressText}
                            </Text>
                            <Text
                                variant="body"
                                className="hds-mb-20-24"
                            >{`${postCode} ${selectedCity}`}</Text>
                            <Text variant="body" className="hds-mb-8-12">
                                {nextDeliveryWeekMsg}
                            </Text>
                            <UnorderedList>
                                {nextDeliveryDays.map((item, index) => {
                                    return <li key={index}>{item}</li>;
                                })}
                            </UnorderedList>
                        </div>
                        <Button data-color="posten" onClick={onReset}>
                            {resetBtnLabel}
                        </Button>
                    </>
                ) : (
                    <>
                        <Alert data-color="warning" className="hds-mb-20-24">
                            <Alert.Description>
                                {deliveryDaysErrorMsg || postCodeErrorMsg}
                            </Alert.Description>
                        </Alert>
                        <Button data-color="posten" onClick={onReset}>
                            {backText}
                        </Button>
                    </>
                )}
            </Box>
        );
    }

    return (
        <Box variant="lighter">
            <Text variant="h3-title" as="h3" className="hds-mb-20-24">
                {title}
            </Text>
            <Text variant="body" className="hds-mb-20-24">
                {description}
            </Text>

            {isLoading ? (
                <Spinner size="medium" />
            ) : (
                <form onSubmit={onSendReq}>
                    <Input
                        validationMessage={postCodeError ? postCodeErrorMsg : ""}
                        data-color={postCodeError ? "error" : ""}
                        label={postCodeLabel}
                        className="hds-mb-8-12"
                        type="tel"
                        maxLength="4"
                        value={postCode}
                        onChange={onPostCodeInput}
                        onKeyDown={handleKeyPress}
                        style={{ width: "183px" }}
                    />
                    <Button data-color="posten" type="submit">
                        {submitBtnLabel}
                    </Button>
                </form>
            )}
        </Box>
    );
};
